// src/MenuKiosk.jsx
//
// DINING-HALL MENU KIOSK
// ----------------------
// Read-only view designed for a TV / tablet / computer left running in the
// dining hall. Gated by a dedicated kiosk PIN (see users.jsx: isKiosk=true).
//
// - Shows TODAY's menu front-and-center, TOMORROW underneath (smaller)
// - Uses the same Menu sheet the chef edits in ChefDashboard
// - Respects special-dates labels (e.g. "Brunch" on Stag Saturday) and hides
//   meals that aren't offered that day (e.g. no Monday/Tuesday breakfast)
// - Auto-refreshes the menu every 5 minutes
// - Updates the clock every 30s and picks a new "today" automatically at midnight
// - No tabs, no member data, no buttons to press. A small Exit button (bottom-right)
//   lets staff log out if needed.
//
// Safe to leave running unattended — there is no access to member info from here.

import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, todayCST, cstFmtYMD } from './config';
import { getMealConfigForDate, getSpecialNoteForDate, ymdToLocalDate } from './specialDates';

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
const CLOCK_MS = 30 * 1000;       // 30 seconds
const MESSAGES_REFRESH_MS = 2 * 60 * 1000; // 2 minutes — pick up admin updates sooner

// Which audiences the kiosk is allowed to display. The dining hall is members-
// only space, so both "all" and "members" are fine. "board" is private.
const KIOSK_OK_AUDIENCES = new Set(['', 'all', 'everyone', 'members', 'member']);

function isMessageActiveToday(msg, today) {
  const s = String(msg.startDate || '').trim();
  const e = String(msg.endDate || '').trim();
  if (s && today < s) return false;
  if (e && today > e) return false;
  return true;
}

function formatClock(d) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function formatLongDate(ymd) {
  const d = ymdToLocalDate(ymd);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

function formatShortDate(ymd) {
  const d = ymdToLocalDate(ymd);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

function nextDay(ymd) {
  const d = ymdToLocalDate(ymd);
  d.setDate(d.getDate() + 1);
  return cstFmtYMD.format(d);
}

export default function MenuKiosk() {
  const [today, setToday] = useState(() => todayCST());
  const [now, setNow] = useState(() => new Date());
  const [menuByDate, setMenuByDate] = useState({});
  const [lastFetched, setLastFetched] = useState(null);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);

  // Clock ticker — also handles midnight rollover by recomputing today()
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
      const t = todayCST();
      if (t !== today) setToday(t);
    }, CLOCK_MS);
    return () => clearInterval(id);
  }, [today]);

  // Data loader — hits the Menu sheet
  const fetchMenu = async () => {
    try {
      const res = await fetch(sheetUrl('Menu'), { cache: 'no-store' });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const map = {};
      (data || []).forEach((row) => {
        const d = String(row.date || '').trim();
        if (!d) return;
        map[d] = {
          breakfast: String(row.breakfast || '').trim(),
          lunch: String(row.lunch || '').trim(),
          dinner: String(row.dinner || '').trim(),
          notes: String(row.chefs_note || '').trim(),
        };
      });
      setMenuByDate(map);
      setLastFetched(new Date());
      setError('');
    } catch (e) {
      console.error('MenuKiosk fetch error', e);
      setError(String(e?.message || e));
    }
  };

  useEffect(() => {
    fetchMenu();
    const id = setInterval(fetchMenu, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin-messages ticker — same sheet AdminMessages writes to
  const fetchMessages = async () => {
    try {
      const res = await fetch(sheetUrl('Messages'), { cache: 'no-store' });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal — just don't show a ticker if the fetch fails
      console.error('MenuKiosk fetchMessages error', e);
    }
  };

  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, MESSAGES_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter to currently-active messages the kiosk is allowed to display
  const activeMessages = useMemo(() => {
    return (messages || [])
      .filter((m) => {
        const aud = String(m.audience || '').trim().toLowerCase();
        if (!KIOSK_OK_AUDIENCES.has(aud)) return false;
        if (!isMessageActiveToday(m, today)) return false;
        const hasContent = (m.title || '').trim() || (m.body || '').trim();
        return !!hasContent;
      })
      .sort((a, b) => {
        const ta = String(a.timestamp || '');
        const tb = String(b.timestamp || '');
        return tb.localeCompare(ta); // newest first
      });
  }, [messages, today]);

  const logout = () => {
    localStorage.removeItem('authenticatedUser');
    window.location.reload();
  };

  const tomorrow = useMemo(() => nextDay(today), [today]);
  const todayMenu = menuByDate[today];
  const tomorrowMenu = menuByDate[tomorrow];

  const todayCfg = getMealConfigForDate(today);
  const tomorrowCfg = getMealConfigForDate(tomorrow);
  const todayNote = getSpecialNoteForDate(today);

  return (
    <div style={S.page}>
      <div style={S.topBanner}>
        <div>
          <div style={S.kicker}>Wauhillau Club</div>
          <div style={S.dateBig}>{formatLongDate(today)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={S.clock}>{formatClock(now)}</div>
          {todayCfg.banner && (
            <div style={S.bannerPill}>{todayCfg.banner}</div>
          )}
        </div>
      </div>

      {/* Today */}
      <section style={S.todaySection}>
        <div style={S.sectionLabel}>TODAY</div>

        {!todayMenu ? (
          <div style={S.emptyCard}>
            <div style={S.emptyBig}>Menu not posted yet</div>
            <div style={S.emptyMuted}>Check back later — chef is still planning.</div>
          </div>
        ) : (
          <div style={S.mealGrid}>
            <MealCard
              size="big"
              enabled={todayCfg.enabled.breakfast}
              label={todayCfg.label.breakfast || 'Breakfast'}
              body={todayMenu.breakfast}
            />
            <MealCard
              size="big"
              enabled={todayCfg.enabled.lunch}
              label={todayCfg.label.lunch || 'Lunch'}
              body={todayMenu.lunch}
            />
            <MealCard
              size="big"
              enabled={todayCfg.enabled.dinner}
              label={todayCfg.label.dinner || 'Dinner'}
              body={todayMenu.dinner}
            />
          </div>
        )}

        {todayMenu && todayMenu.notes && (
          <div style={S.chefsNote}>
            <span style={S.chefsNoteLabel}>Chef's note</span>
            <span style={S.chefsNoteBody}>{todayMenu.notes}</span>
          </div>
        )}

        {todayNote && !todayCfg.banner && (
          <div style={{ ...S.chefsNote, borderColor: 'rgba(14,165,233,0.5)' }}>
            <span style={{ ...S.chefsNoteLabel, color: '#7dd3fc' }}>Note</span>
            <span style={S.chefsNoteBody}>{todayNote}</span>
          </div>
        )}
      </section>

      {/* Tomorrow */}
      <section style={S.tomorrowSection}>
        <div style={S.sectionLabel}>
          TOMORROW · {formatShortDate(tomorrow)}
          {tomorrowCfg.banner && <span style={S.inlineBanner}> · {tomorrowCfg.banner}</span>}
        </div>

        {!tomorrowMenu ? (
          <div style={S.tomorrowEmpty}>Menu not posted yet.</div>
        ) : (
          <div style={S.mealGrid}>
            <MealCard
              size="small"
              enabled={tomorrowCfg.enabled.breakfast}
              label={tomorrowCfg.label.breakfast || 'Breakfast'}
              body={tomorrowMenu.breakfast}
            />
            <MealCard
              size="small"
              enabled={tomorrowCfg.enabled.lunch}
              label={tomorrowCfg.label.lunch || 'Lunch'}
              body={tomorrowMenu.lunch}
            />
            <MealCard
              size="small"
              enabled={tomorrowCfg.enabled.dinner}
              label={tomorrowCfg.label.dinner || 'Dinner'}
              body={tomorrowMenu.dinner}
            />
          </div>
        )}
      </section>

      {/* Announcements ticker */}
      {activeMessages.length > 0 && <AnnouncementsTicker messages={activeMessages} />}

      {/* Footer */}
      <div style={S.footer}>
        <div style={S.footerMuted}>
          {error
            ? `Last fetch failed: ${error.slice(0, 80)}`
            : lastFetched
            ? `Updated ${formatClock(lastFetched)}`
            : 'Loading…'}
          {' · Auto-refreshes every 5 min'}
        </div>
        <button style={S.exitBtn} onClick={logout} title="Exit kiosk mode">
          Exit
        </button>
      </div>
    </div>
  );
}

function AnnouncementsTicker({ messages }) {
  // Build a long string of "TITLE — BODY   •   TITLE — BODY   •   ..." and
  // duplicate it so the marquee loop is seamless.
  const joined = messages
    .map((m) => {
      const t = String(m.title || '').trim();
      const b = String(m.body || '').trim().replace(/\s+/g, ' ');
      if (t && b) return `${t} — ${b}`;
      return t || b;
    })
    .filter(Boolean)
    .join('   •   ');

  if (!joined) return null;

  // Slow it down when there are more characters (so short messages don't zoom past)
  const duration = Math.max(30, Math.min(90, Math.round(joined.length / 8)));

  return (
    <>
      <style>{`
        @keyframes wc-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .wc-ticker-track {
          display: inline-flex;
          gap: 48px;
          animation: wc-ticker-scroll linear infinite;
          will-change: transform;
        }
        .wc-ticker-viewport:hover .wc-ticker-track {
          animation-play-state: paused;
        }
      `}</style>
      <div style={S.tickerWrap} aria-label="Club announcements">
        <div style={S.tickerLabel}>📣 Announcements</div>
        <div style={S.tickerViewport} className="wc-ticker-viewport">
          <div
            className="wc-ticker-track"
            style={{ animationDuration: `${duration}s` }}
          >
            <span style={S.tickerText}>{joined}</span>
            <span style={S.tickerText} aria-hidden="true">{joined}</span>
          </div>
        </div>
      </div>
    </>
  );
}

function MealCard({ size, enabled, label, body }) {
  if (!enabled) return null; // hide cards for meals not offered that day

  const isBig = size === 'big';
  return (
    <div style={isBig ? S.mealCardBig : S.mealCardSmall}>
      <div style={isBig ? S.mealLabelBig : S.mealLabelSmall}>{label}</div>
      <div style={isBig ? S.mealBodyBig : S.mealBodySmall}>
        {body ? body : <span style={S.emptyMeal}>— not posted yet —</span>}
      </div>
    </div>
  );
}

// ---------- styles ----------
const ACCENT = '#0ea5e9';
const GREEN  = '#22c55e';

const S = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(900px 500px at 20% 0%, rgba(14,165,233,0.18), transparent 55%),' +
      'radial-gradient(800px 500px at 80% 10%, rgba(34,197,94,0.16), transparent 60%),' +
      '#0b1220',
    color: '#f8fafc',
    padding: 'clamp(20px, 3vw, 40px)',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  topBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 20,
    flexWrap: 'wrap',
  },
  kicker: {
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#94a3b8',
    fontWeight: 700,
  },
  dateBig: {
    fontSize: 'clamp(28px, 4vw, 52px)',
    fontWeight: 900,
    lineHeight: 1.1,
    marginTop: 4,
  },
  clock: {
    fontSize: 'clamp(36px, 5vw, 64px)',
    fontWeight: 900,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
  },
  bannerPill: {
    display: 'inline-block',
    marginTop: 10,
    padding: '8px 16px',
    borderRadius: 999,
    background: 'rgba(14,165,233,0.18)',
    border: `1px solid ${ACCENT}`,
    color: '#7dd3fc',
    fontWeight: 800,
    fontSize: 'clamp(13px, 1.2vw, 16px)',
    letterSpacing: 0.5,
  },
  todaySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  tomorrowSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 10,
    opacity: 0.9,
  },
  sectionLabel: {
    fontSize: 'clamp(11px, 1vw, 14px)',
    letterSpacing: 3,
    textTransform: 'uppercase',
    color: '#94a3b8',
    fontWeight: 800,
  },
  inlineBanner: {
    color: '#7dd3fc',
    fontWeight: 800,
    marginLeft: 6,
  },
  mealGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
  },
  mealCardBig: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: 'clamp(16px, 2vw, 28px)',
    minHeight: 180,
    boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
  },
  mealCardSmall: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: '14px 16px',
    minHeight: 100,
  },
  mealLabelBig: {
    fontSize: 'clamp(14px, 1.4vw, 18px)',
    fontWeight: 900,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: GREEN,
    marginBottom: 10,
  },
  mealLabelSmall: {
    fontSize: 'clamp(11px, 1vw, 13px)',
    fontWeight: 800,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: GREEN,
    marginBottom: 6,
  },
  mealBodyBig: {
    fontSize: 'clamp(18px, 1.8vw, 26px)',
    lineHeight: 1.35,
    color: '#f1f5f9',
    fontWeight: 500,
    whiteSpace: 'pre-wrap',
  },
  mealBodySmall: {
    fontSize: 'clamp(14px, 1.2vw, 17px)',
    lineHeight: 1.3,
    color: '#cbd5e1',
    whiteSpace: 'pre-wrap',
  },
  emptyMeal: {
    color: '#64748b',
    fontStyle: 'italic',
  },
  emptyCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px dashed rgba(255,255,255,0.18)',
    borderRadius: 20,
    padding: 'clamp(20px, 3vw, 40px)',
    textAlign: 'center',
  },
  emptyBig: {
    fontSize: 'clamp(22px, 2.4vw, 32px)',
    fontWeight: 900,
    color: '#e5e7eb',
  },
  emptyMuted: {
    marginTop: 8,
    color: '#94a3b8',
    fontSize: 'clamp(14px, 1.2vw, 17px)',
  },
  tomorrowEmpty: {
    color: '#64748b',
    fontStyle: 'italic',
    padding: '10px 4px',
  },
  chefsNote: {
    marginTop: 4,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    background: 'rgba(34,197,94,0.08)',
    border: '1px solid rgba(34,197,94,0.35)',
    borderRadius: 14,
  },
  chefsNoteLabel: {
    fontSize: 11,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: '#bbf7d0',
    fontWeight: 800,
    minWidth: 96,
    paddingTop: 2,
  },
  chefsNoteBody: {
    fontSize: 'clamp(14px, 1.2vw, 17px)',
    color: '#e5e7eb',
    lineHeight: 1.4,
  },
  tickerWrap: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '12px 16px',
    background: 'linear-gradient(90deg, rgba(14,165,233,0.18), rgba(34,197,94,0.14))',
    border: '1px solid rgba(14,165,233,0.35)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  tickerLabel: {
    flex: '0 0 auto',
    fontSize: 'clamp(12px, 1.1vw, 15px)',
    fontWeight: 900,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: '#7dd3fc',
    paddingRight: 14,
    borderRight: '1px solid rgba(255,255,255,0.15)',
    whiteSpace: 'nowrap',
  },
  tickerViewport: {
    flex: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maskImage:
      'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
  },
  tickerText: {
    display: 'inline-block',
    fontSize: 'clamp(16px, 1.5vw, 22px)',
    fontWeight: 600,
    color: '#f1f5f9',
    padding: '2px 0',
  },
  footer: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingTop: 20,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  footerMuted: {
    color: '#64748b',
    fontSize: 12,
  },
  exitBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.04)',
    color: '#94a3b8',
    fontSize: 11,
    cursor: 'pointer',
    opacity: 0.7,
  },
};
